import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select,
  Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import wineListService from '../../services/wineListService';
import {
  sortWineListItems,
  WINE_LIST_SORT_LABELS,
  type WineListSortMode,
} from '../../utils/wineListSort';
import {
  EXPORT_SORT_LABELS,
  exportWineListXlsx,
  type ExportSortMode,
} from '../../utils/wineListExport';
import {
  WINE_LIST_SCORE_SOURCES,
  type WineListAvailabilityBand,
  type WineListRender,
  type WineListRenderItem,
  type WineListRenderSection,
} from '../../types/venue';

interface Props {
  venueId: string;
  wineListId: string;
}

const COLOR_DOT: Record<string, string> = {
  RED: '#922B21',
  WHITE: '#c9a227',
  PINK_ROSE: '#c2185b',
};

const COLOR_LABEL: Record<string, string> = {
  RED: 'Red',
  WHITE: 'White',
  PINK_ROSE: 'Rosé',
};

const matchesSearch = (item: WineListRenderItem, q: string): boolean => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    (item.name?.toLowerCase().includes(needle) ?? false) ||
    (item.producer?.toLowerCase().includes(needle) ?? false) ||
    (item.region?.toLowerCase().includes(needle) ?? false)
  );
};

const formatPrice = (item: WineListRenderItem): string | null => {
  if (item.price == null) return null;
  const currencyPart = item.currency ? `${item.currency} ` : '';
  return `${currencyPart}${item.price.toFixed(2)}`;
};

const formatSize = (sizeMl: number | null): string | null => {
  if (sizeMl == null) return null;
  return sizeMl >= 1000 ? `${sizeMl / 1000}L` : `${sizeMl} ml`;
};

/**
 * Colour the staff line by availability band.
 * Subtle by default; LOW_STOCK / OUT_OF_STOCK use the theme's warning / disabled tones.
 */
const STAFF_LINE_COLOR: Record<WineListAvailabilityBand, string | undefined> = {
  IN_STOCK: undefined,        // muted text.secondary
  LOW_STOCK: '#b15c00',       // amber
  OUT_OF_STOCK: '#a1a1a1',    // disabled
  UNKNOWN: undefined,
};

/** Format a price + currency in the same way as bottle pricing. */
const formatFormatPrice = (price: number, currency: string | null): string => {
  const currencyPart = currency ? `${currency} ` : '';
  return `${currencyPart}${price.toFixed(2)}`;
};

const WineCard: React.FC<{ item: WineListRenderItem }> = ({ item }) => {
  const theme = useTheme();
  const colorDot = item.color ? COLOR_DOT[item.color] : null;
  const colorLabel = item.color ? (COLOR_LABEL[item.color] ?? item.color) : null;
  const bottlePriceLabel = formatPrice(item);
  const size = formatSize(item.bottleSize);

  // W3 pricing block. The right-hand column shows:
  //   - Bottle row first (always, when bottlePrice is present)
  //   - Each configured format in catalog position order (server-sorted)
  // If neither bottle price nor any format pricing exist, show an em-dash.
  const hasAnyPricing = bottlePriceLabel !== null || (item.formats && item.formats.length > 0);

  // W4 bottle availability — derived from inventory, safe for customer view.
  // Renders nothing when the wine has no inventory link (bottlesAvailable === null).
  // Format-aware availability (glass, pours) will be implemented in a future phase.
  const bottlesAvailable = item.bottlesAvailable;
  let availabilityText: string | null = null;
  if (bottlesAvailable != null) {
    if (bottlesAvailable === 0) availabilityText = 'Out of stock';
    else if (bottlesAvailable === 1) availabilityText = '1 bottle';
    else availabilityText = `${bottlesAvailable} bottles`;
  }
  const availabilityColor = bottlesAvailable != null && bottlesAvailable <= 3
    ? '#b15c00' // amber for low / out
    : theme.palette.text.secondary;

  // STAFF-ONLY overlay: bin number + stock. These come from the admin render
  // endpoint and must NOT be rendered in any future public/customer view of
  // this component (or its DTO). Both fields are optional on the type — a
  // public DTO will simply omit them and this block will render nothing.
  const binPart = item.binNumber ? `Bin ${item.binNumber}` : null;
  const stockPart = item.stockLabel ?? null;
  const staffLine = [binPart, stockPart].filter(Boolean).join(' · ');
  const staffColor = item.availabilityBand
    ? STAFF_LINE_COLOR[item.availabilityBand]
    : undefined;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {item.name ?? 'Unknown wine'}
          </Typography>
          {item.vintage && (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {item.vintage}
            </Typography>
          )}
        </Box>
        {(item.producer || item.region) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.producer}
            {item.producer && item.region ? ' · ' : ''}
            {item.region}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {colorDot && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorDot }} />
              <Typography variant="caption" color="text.secondary">{colorLabel}</Typography>
            </Box>
          )}
          {size && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {size}
            </Typography>
          )}
        </Box>
        {item.notes && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.25, fontStyle: 'italic', borderLeft: `2px solid ${alpha(theme.palette.divider, 0.4)}`, pl: 1.25 }}
          >
            {item.notes}
          </Typography>
        )}
      </Box>
      {hasAnyPricing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0, minWidth: 90 }}>
          {/* Critic score chips (W8). Source order: JS, WA, WS, AV. Skipped
              entirely when item.scores is empty/undefined. Rendered above the
              bottle price so the score badges read like a header. */}
          {item.scores && item.scores.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {WINE_LIST_SCORE_SOURCES.map(src => {
                const entry = item.scores?.find(e => e.source === src);
                if (!entry) return null;
                return (
                  <Chip
                    key={src}
                    label={`${src} ${entry.score}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 20,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                );
              })}
            </Box>
          )}
          {bottlePriceLabel && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.2 }}>
                Bottle
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                {bottlePriceLabel}
              </Typography>
            </Box>
          )}
          {item.formats?.map(fmt => (
            <Box key={`${fmt.name}-${fmt.volumeMl}`} sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.06em', lineHeight: 1.2 }}>
                {fmt.name}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.68rem', lineHeight: 1.2 }}>
                {fmt.volumeMl} ml
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                {formatFormatPrice(fmt.price, item.currency)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )}
      </Box>
      {/* Footer: bottle availability (W4, customer-safe) + staff line (W1B, admin-only). */}
      {(availabilityText || staffLine) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {availabilityText && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '0.72rem',
                letterSpacing: '0.02em',
                color: availabilityColor,
                fontWeight: bottlesAvailable != null && bottlesAvailable <= 3 ? 600 : 500,
              }}
            >
              {availabilityText}
            </Typography>
          )}
          {/* STAFF-ONLY line — admin preview only. Public render would omit these fields entirely. */}
          {staffLine && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.02em',
                color: staffColor ?? theme.palette.text.disabled,
              }}
            >
              {staffLine}
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

const WineListPreviewPanel: React.FC<Props> = ({ venueId, wineListId }) => {
  const theme = useTheme();
  const [render, setRender] = useState<WineListRender | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [sortMode, setSortMode] = useState<WineListSortMode>('DEFAULT');
  const [search, setSearch] = useState('');
  /** null = all sections; string = section id (or 'unsectioned' for the synthetic bucket) */
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Designer XLSX export — dialog state + chosen sort mode. Default is the
  // most useful for a chronological wine-list layout: oldest vintages first.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState<ExportSortMode>('vintage-asc');

  const handleExportDownload = () => {
    if (!render) return;
    exportWineListXlsx(render, exportMode);
    setExportOpen(false);
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    wineListService.renderWineList(venueId, wineListId)
      .then(setRender)
      .catch(err => {
        const msg = err?.response?.data?.message ?? 'Could not load preview.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [venueId, wineListId]);

  // Filter + sort, applied per visible section bucket.
  const visibleSections = useMemo<WineListRenderSection[]>(() => {
    if (!render) return [];
    // Hide HIDDEN sections from preview entirely (they would not show on the customer view).
    let sections = render.sections.filter(s => s.visibility === 'LIVE');
    if (activeSection !== null) {
      sections = sections.filter(s => (s.id ?? 'unsectioned') === activeSection);
    }
    return sections.map(s => ({
      ...s,
      items: sortWineListItems(s.items.filter(item => matchesSearch(item, search.trim())), sortMode),
    }));
  }, [render, activeSection, search, sortMode]);

  const totalVisibleItems = visibleSections.reduce((acc, s) => acc + s.items.length, 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  if (!render) return null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 760, mx: 'auto' }}>
      {/* List heading */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {render.list.name}
        </Typography>
        {render.list.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {render.list.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          Preview · {render.list.status}
        </Typography>
      </Box>

      {/* Search + sort controls */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1,
        backgroundColor: theme.palette.background.default,
        py: 1.5, mb: 1.5,
      }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search wines, producers, regions…"
            sx={{ flex: 1, minWidth: 220 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              value={sortMode}
              label="Sort"
              onChange={e => setSortMode(e.target.value as WineListSortMode)}
            >
              {(Object.keys(WINE_LIST_SORT_LABELS) as WineListSortMode[]).map(mode => (
                <MenuItem key={mode} value={mode}>{WINE_LIST_SORT_LABELS[mode]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportOpen(true)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Export
          </Button>
        </Box>

        {/* Section chips */}
        {render.sections.length > 1 && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              size="small"
              variant={activeSection === null ? 'filled' : 'outlined'}
              color={activeSection === null ? 'primary' : 'default'}
              onClick={() => setActiveSection(null)}
              sx={{ fontWeight: 600 }}
            />
            {render.sections
              .filter(s => s.visibility === 'LIVE')
              .map(s => {
                const id = s.id ?? 'unsectioned';
                return (
                  <Chip
                    key={id}
                    label={s.name}
                    size="small"
                    variant={activeSection === id ? 'filled' : 'outlined'}
                    color={activeSection === id ? 'primary' : 'default'}
                    onClick={() => setActiveSection(id)}
                  />
                );
              })}
          </Box>
        )}
      </Box>

      {/* Counts line */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {totalVisibleItems} {totalVisibleItems === 1 ? 'wine' : 'wines'}
        {search.trim() ? ` matching "${search.trim()}"` : ''}
      </Typography>

      {/* Sections */}
      {totalVisibleItems === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {search.trim() || activeSection !== null
              ? 'No wines match the current filters.'
              : 'No live wines on this list yet.'}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {visibleSections.map(section => (
            section.items.length === 0 ? null : (
              <Box key={section.id ?? 'unsectioned'}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: theme.palette.text.primary }}>
                    {section.name}
                  </Typography>
                  {section.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {section.description}
                    </Typography>
                  )}
                </Box>
                <Stack spacing={1.25}>
                  {section.items.map(item => (
                    <WineCard key={item.id} item={item} />
                  ))}
                </Stack>
              </Box>
            )
          ))}
        </Stack>
      )}

      {/* Designer XLSX export dialog. Pulls from the already-fetched render
          DTO, so no network round-trip — just sort + generate locally. */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export wine list</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download an Excel sheet with one row per wine, grouped by section.
            Items inside each section are sorted by the option below.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Sort items within section</InputLabel>
            <Select
              value={exportMode}
              label="Sort items within section"
              onChange={e => setExportMode(e.target.value as ExportSortMode)}
            >
              {(Object.keys(EXPORT_SORT_LABELS) as ExportSortMode[]).map(mode => (
                <MenuItem key={mode} value={mode}>{EXPORT_SORT_LABELS[mode]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleExportDownload}
            variant="contained"
            startIcon={<FileDownloadIcon />}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WineListPreviewPanel;
